import { haversine, normLon } from "./geo";

const esc = (s: string) =>
  s.replace(
    /[<>&"']/g,
    (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" })[c] as string,
  );

/** GPX 1.1 track for a route polyline ([lat, lon] pairs, optional elevations). */
export function routeToGpx(name: string, coords: [number, number][], ele?: number[]): string {
  const pts = coords
    .map(([lat, lon], i) => {
      const e = ele?.[i];
      return `      <trkpt lat="${lat.toFixed(7)}" lon="${normLon(lon).toFixed(7)}">${e !== undefined ? `<ele>${e.toFixed(1)}</ele>` : ""}</trkpt>`;
    })
    .join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="MakeATrail" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${esc(name)}</name>
    <copyright author="OpenStreetMap contributors"><license>https://opendatacommons.org/licenses/odbl/</license></copyright>
  </metadata>
  <trk>
    <name>${esc(name)}</name>
    <type>running</type>
    <trkseg>
${pts}
    </trkseg>
  </trk>
</gpx>
`;
}

/**
 * Elevation per polyline point from a per-node profile (`d` = metres along the route). The
 * profile has one sample per graph node, the polyline more points (edge geometry), so distance
 * along the polyline is scaled to the profile's length and interpolated linearly.
 * Undefined when the profile has no finite samples.
 */
export function elevationAlong(
  coords: readonly [number, number][],
  profile: readonly { d: number; ele: number }[],
): number[] | undefined {
  if (coords.length === 0 || profile.length === 0 || profile.some((p) => !Number.isFinite(p.ele))) {
    return undefined;
  }
  const cum: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1] as [number, number];
    const b = coords[i] as [number, number];
    cum.push((cum[i - 1] as number) + haversine(a[0], a[1], b[0], b[1]));
  }
  const total = cum[cum.length - 1] as number;
  const pTotal = (profile[profile.length - 1] as { d: number }).d;
  const out: number[] = [];
  let j = 0;
  for (const c of cum) {
    const d = total > 0 ? (c / total) * pTotal : 0;
    while (j < profile.length - 2 && (profile[j + 1] as { d: number }).d < d) j++;
    const p0 = profile[j] as { d: number; ele: number };
    const p1 = profile[Math.min(j + 1, profile.length - 1)] as { d: number; ele: number };
    const span = p1.d - p0.d;
    const t = span > 0 ? Math.min(1, Math.max(0, (d - p0.d) / span)) : 0;
    out.push(p0.ele + (p1.ele - p0.ele) * t);
  }
  return out;
}
